'use client'

import { createContext, useContext, useState } from 'react'
import { togglesDictionary } from '../dictionary'
import type { TogglesDictionary } from '../types'
import { fetchToggles } from '../use-cases'
import type { TogglesContextType } from './types'
import { createGlovoError, getErrorMessage } from '@/tools/error-handling'
import { fetcher } from '@/tools/fetcher/client'
import { glovoRum } from '@/tools/o11y/client'

const TogglesContext = createContext<TogglesContextType>({
  toggles: togglesDictionary,
  refreshToggles: async () => undefined,
})

export function TogglesProvider({
  children,
  initialState,
}: {
  children: React.ReactNode
  initialState: TogglesDictionary
}) {
  const [toggles, setToggles] = useState<TogglesDictionary>(initialState)

  const onError = (error: unknown) => {
    const glovoError = createGlovoError(error)
    const errorMessage = getErrorMessage(error)
    glovoRum.addError(glovoError, {
      id: 'toggles_fetch_error',
      errorMessage: errorMessage,
      error: glovoError,
    })
  }

  const refreshToggles = async () => {
    const response = await fetchToggles(fetcher, onError)
    setToggles(response)
  }

  const value: TogglesContextType = {
    toggles,
    refreshToggles,
  }

  return <TogglesContext.Provider value={value}>{children}</TogglesContext.Provider>
}

export function useToggles() {
  const context = useContext(TogglesContext)
  if (!context) {
    throw new Error('useToggles must be used inside a <TogglesContext>')
  }
  return context
}
